import apiClient from './apiClient';

export interface Guidance {
  themeId: string;
  themeLabel: string | null;
  sportIdentifier: string;
  coachNote: string | null;
  nutrition: string | null;
  warmupPattern: string | null;
  cooldownPattern: string | null;
}

export interface GuidanceList {
  items: Guidance[];
  total: number;
}

export interface GuidanceCreatePayload {
  themeId: string;
  sportIdentifier: string;
  coachNote?: string | null;
  nutrition?: string | null;
  warmupPattern?: string | null;
  cooldownPattern?: string | null;
}

export interface ImportRow {
  theme: string;
  sport: string;
  coachNote?: string | null;
  nutrition?: string | null;
  warmupPattern?: string | null;
  cooldownPattern?: string | null;
}

export interface ImportReportRow {
  index: number;
  theme: string;
  sport: string;
  status: 'ok' | 'error';
  reason?: string;
  themeLabel?: string;
  sportIdentifier?: string;
}

export interface ImportReport {
  dryRun: boolean;
  summary: { total: number; resolved: number; unresolved: number; imported: number };
  rows: ImportReportRow[];
}

export const getGuidance = async (filters: {
  search?: string;
  sport?: string;
  page?: number;
  pageSize?: number;
}): Promise<GuidanceList> => {
  const response = await apiClient.get('admin/guidance', { params: filters });
  return response.data as GuidanceList;
};

export const createGuidance = async (payload: GuidanceCreatePayload): Promise<Guidance> => {
  const response = await apiClient.post('admin/guidance', payload);
  return response.data as Guidance;
};

export const updateGuidance = async (
  themeId: string,
  sportIdentifier: string,
  payload: {
    coachNote?: string | null;
    nutrition?: string | null;
    warmupPattern?: string | null;
    cooldownPattern?: string | null;
  }
): Promise<Guidance> => {
  const response = await apiClient.put(`admin/guidance/${themeId}/${sportIdentifier}`, payload);
  return response.data as Guidance;
};

export const deleteGuidance = async (themeId: string, sportIdentifier: string): Promise<void> => {
  await apiClient.delete(`admin/guidance/${themeId}/${sportIdentifier}`);
};

export const importGuidance = async (rows: ImportRow[], dryRun: boolean): Promise<ImportReport> => {
  const response = await apiClient.post('admin/guidance/import', { rows, dryRun });
  return response.data as ImportReport;
};

export interface CompetitionGuidance {
  formatId: string;
  formatLabel: string;
  formatSlug: string;
  raceTypeId: number;
  raceTypeLabel: string;
  raceTypeIdentifier: string;
  coachNote: string | null;
  nutrition: string | null;
  updatedAt: string | null;
}

export const getCompetitionGuidance = async (): Promise<CompetitionGuidance[]> => {
  const response = await apiClient.get('admin/guidance/competition');
  return response.data as CompetitionGuidance[];
};

export const saveCompetitionGuidance = async (
  formatId: string,
  payload: { coachNote?: string | null; nutrition?: string | null }
): Promise<CompetitionGuidance> => {
  const response = await apiClient.put(`admin/guidance/competition/${formatId}`, payload);
  return response.data as CompetitionGuidance;
};

export const deleteCompetitionGuidance = async (formatId: string): Promise<void> => {
  await apiClient.delete(`admin/guidance/competition/${formatId}`);
};
